import * as React from "react";
import { Link } from "react-router-dom";
import {
  Space,
  Table,
  Skeleton,
  message,
  Tag,
  Button,
} from "antd";
import { CloudDownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { ListPageLayout } from "layouts";
import { formatFullDateFromTimeStamp, SubmissionApiCommunicator } from "api";
import { downloadSubmissionOriginalFile } from "utils/downloadUtils";
import { getStatusColor } from "utils/statusUtils";

/**
 * SubmissionListPage Component
 * 
 * Displays a paginated table of all submissions with their current status and basic information.
 * Provides quick actions for viewing details and downloading files.
 * 
 * Features:
 * - Sortable table with pagination
 * - Color-coded status tags for submission and part vector search status
 * - Quick action buttons for viewing details and downloading files
 * - Real-time updates when new submissions are added
 * 
 * @returns {JSX.Element} The submission list page component
 */
export function SubmissionListPage() {
  const [submissionApiCommunicator] = React.useState(new SubmissionApiCommunicator());
  const [loading, setLoading] = React.useState(false);
  /** @type {ReturnType<typeof React.useState<Array>>} */
  const [submissions, setSubmissions] = React.useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchPageData = React.useCallback(async () => {
    
    try {
      setLoading(true);
      const res = await submissionApiCommunicator.list();
      console.log("res.data", res.data);
      let submissionsArr;
      if (Array.isArray(res.data?.data)) {
        if (res.data.data.length === 0) {
          console.log("SubmissionListPage: res.data.data is empty", res.data.data);
        } else {
          console.log("SubmissionListPage: res.data.data is not empty", res.data.data);
        }
        submissionsArr = res.data.data;
      } else {
        console.log("SubmissionListPage: res.data.data is not an array", res.data.data);
        submissionsArr = [];
      }
      setSubmissions(submissionsArr.sort((a, b) => b.createdAt - a.createdAt));
    } catch (err) {
      messageApi.error(err.message);
    }
    setLoading(false);
  }, [messageApi, submissionApiCommunicator]);

  React.useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);




  const downloadOriginalFile = React.useCallback(async (record) => {
    setLoading(true);
    await downloadSubmissionOriginalFile(
      record,
      (message) => messageApi.info(message),
      (message) => messageApi.success(message),
      (message) => messageApi.error(message)
    );
    setLoading(false);
  }, [messageApi]);

  const columns = [
    {
      title: "File Name",
      dataIndex: ["partVectorSearch", "fileName"],
      key: "fileName",
      render: (text, record) => (
        <Link to={`/submissions/${record.partSearchSubmissionId}`}>
          {text || 'Unknown File'}
        </Link>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let displayText;
        if (status === 'scan_uploaded') {
          displayText = 'Scan Uploaded';
        } else {
          displayText = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
        }
        return (
          <Tag color={getStatusColor(status, 'submission')}>
            {displayText}
          </Tag>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => formatFullDateFromTimeStamp(createdAt),
    },
    {
      title: "Part Vector Search Status",
      dataIndex: ["partVectorSearch", "status"],
      key: "partVectorSearchStatus",
      render: (status) => (
        <Tag color={getStatusColor(status, 'partVectorSearch')}>
          {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<SearchOutlined />}
            onClick={() => window.location.href = `/submissions/${record.partSearchSubmissionId}`}
            size="small"
          />
          {record.partVectorSearch?.files?.original && (
            <Button
              type="link"
              icon={<CloudDownloadOutlined />}
              onClick={() => downloadOriginalFile(record)}
              disabled={loading}
              size="small"
            />
          )}
        </Space>
      ),
    },
  ];


  return (
    <ListPageLayout>
      {contextHolder}
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          {loading ? (
            <Skeleton active />
          ) : (
            <Table
              dataSource={submissions}
              columns={columns}
              rowKey="partSearchSubmissionId"
              loading={loading}
              emptyText="No data"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          )}
        </div>
      </Space>
    </ListPageLayout>
  );
} 